/**
 * Schedule Timeline (#946) — forward projection of scheduled connector runs.
 *
 * Entry point mirrors Connector Statistics: reached from a link on the
 * Connectors page, with a Back to Connectors button and no top-nav entry.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDuration } from '@/utils/formatDuration';
import { cn } from '@/lib/utils';
import { useScheduleForecast } from '@/hooks/useScheduleForecast';
import { ConcurrencyRibbon } from '@/components/connectors/schedule/ConcurrencyRibbon';
import { ContentionChips } from '@/components/connectors/schedule/ContentionChips';
import { NotProjectedTable } from '@/components/connectors/schedule/NotProjectedTable';
import { ScheduleTimeline } from '@/components/connectors/schedule/ScheduleTimeline';
import { describeCron } from '@/lib/cron/describe';
import {
  CONTINUOUS_MAX_GAP_MIN,
  UNKNOWN_NOMINAL_SEC,
  contentionRanges,
  floorToMinute,
  longestClearWindow,
  occupancyDepth,
  type ProjectedLane,
} from '@/components/connectors/schedule/scheduleProjection';
import { formatInstant } from '@/components/connectors/schedule/scheduleTime';

const MIN = 60_000;
const HORIZONS = [
  { hours: 24, label: 'Next 24 hours' },
  { hours: 168, label: 'Next 7 days' },
];

type ZoomRange = { start: number; end: number } | null;

const Segmented: React.FC<{
  label: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, options, value, onChange }) => (
  <div>
    <span className="mb-1.5 block text-xs text-muted-foreground">{label}</span>
    <div className="inline-flex gap-0.5 rounded-md bg-muted p-0.5" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-sm px-3 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            value === o.value
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

const Stat: React.FC<{ label: string; value: string; note?: string }> = ({ label, value, note }) => (
  <div className="bg-card p-4 px-5">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="mt-1 text-2xl font-bold tracking-tight tabular-nums">{value}</div>
    {note && <div className="mt-0.5 text-xs text-muted-foreground">{note}</div>}
  </div>
);

export const ScheduleTimelinePage: React.FC = () => {
  const [horizonHours, setHorizonHours] = useState(24);
  const [useLocalTime, setUseLocalTime] = useState(false);
  const [zoom, setZoom] = useState<ZoomRange>(null);

  const { data, isLoading } = useScheduleForecast({ horizonHours });

  const displayTimezone = useLocalTime
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : (data?.schedule_timezone ?? 'America/New_York');

  const model = useMemo(() => {
    if (!data) return null;

    // Floored to the minute so the origin shared by the per-minute bucketing and
    // by every index→timestamp label below sits on the wall clock (#1030). Both
    // bounds, so a 24h horizon stays exactly 1440 buckets.
    const windowStart = floorToMinute(Date.parse(data.window_start));
    const windowEnd = floorToMinute(Date.parse(data.window_end));

    const lanes: ProjectedLane[] = data.connectors.map((c) => {
      const starts = c.run_starts.map((s) => Date.parse(s));
      const durationMs = (c.median_duration_seconds ?? UNKNOWN_NOMINAL_SEC) * 1000;
      const gapMin = starts.length > 1 ? (starts[1] - starts[0]) / MIN : Infinity;
      return {
        configId: c.connector_config_id,
        name: c.name,
        connectorType: c.connector_type,
        cron: c.cron_expression,
        timezone: c.timezone,
        medianSeconds: c.median_duration_seconds,
        p90Seconds: c.p90_duration_seconds,
        sampleCount: c.sample_count,
        runs: starts.map((start) => ({
          start,
          end: start + durationMs,
          unknown: c.median_duration_seconds === null,
        })),
        continuous: gapMin <= CONTINUOUS_MAX_GAP_MIN,
      };
    });

    const events = lanes.filter((l) => !l.continuous);
    const continuous = lanes.filter((l) => l.continuous);
    const eventDepth = occupancyDepth(events, windowStart, windowEnd);
    const baselineDepth = occupancyDepth(continuous, windowStart, windowEnd);
    const totalDepth = eventDepth.map((v, i) => v + baselineDepth[i]);
    const ranges = contentionRanges(totalDepth, eventDepth).map(([from, to]) => ({
      from,
      to,
      peak: Math.max(...totalDepth.slice(from, to)),
    }));
    const clear = longestClearWindow(eventDepth);
    const peakIndex = totalDepth.indexOf(Math.max(0, ...totalDepth));

    return {
      windowStart,
      windowEnd,
      lanes,
      events,
      continuous,
      eventDepth,
      baselineDepth,
      totalDepth,
      ranges,
      clear,
      peakIndex,
      runCount: lanes.reduce((n, l) => n + l.runs.length, 0),
    };
  }, [data]);

  const viewStart = zoom?.start ?? model?.windowStart ?? 0;
  const viewEnd = zoom?.end ?? model?.windowEnd ?? 0;

  const handleZoom = (start: number | null, end?: number) =>
    setZoom(start === null || end === undefined ? null : { start, end });

  return (
    <div className="container mx-auto space-y-6 px-6 py-8">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/connectors">
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back to Connectors
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Schedule Timeline</h1>
          <p className="mt-1 text-muted-foreground">
            Projected connector runs from the current schedule settings, sized by median run time.
          </p>
        </div>
        <div className="flex flex-wrap items-start gap-4">
          <Segmented
            label="Horizon"
            value={String(horizonHours)}
            options={HORIZONS.map((h) => ({ value: String(h.hours), label: h.label }))}
            onChange={(v) => {
              setHorizonHours(Number(v));
              setZoom(null);
            }}
          />
          <Segmented
            label="Time zone"
            value={useLocalTime ? 'local' : 'schedule'}
            options={[
              { value: 'schedule', label: 'Eastern' },
              { value: 'local', label: 'Your local time' },
            ]}
            onChange={(v) => setUseLocalTime(v === 'local')}
          />
        </div>
      </div>

      {isLoading || !model || !data ? (
        <Skeleton className="h-[420px] w-full" />
      ) : model.lanes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-center">
            <CalendarClock className="h-16 w-16 text-muted-foreground" />
            <h2 className="mt-4 text-xl font-semibold">No scheduled runs to project</h2>
            <p className="mt-2 max-w-md text-muted-foreground">
              Enable a schedule on a connector to see when its runs land and how long they occupy
              the window.
            </p>
            <Button asChild className="mt-6">
              <Link to="/connectors">Go to Connectors</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border md:grid-cols-4">
            <Stat
              label="Scheduled connectors"
              value={String(model.lanes.length)}
              note={`${data.excluded.length} not projected`}
            />
            <Stat
              label="Projected runs"
              value={String(model.runCount)}
              note={`in the ${horizonHours === 24 ? 'next 24 hours' : 'next 7 days'}`}
            />
            <Stat
              label="Peak concurrency"
              value={`${Math.max(0, ...model.totalDepth)} at once`}
              note={formatInstant(model.windowStart + model.peakIndex * MIN, displayTimezone)}
            />
            <Stat
              label="Longest clear window"
              value={formatDuration(model.clear.lengthMinutes * 60)}
              note={
                model.clear.lengthMinutes
                  ? `from ${formatInstant(model.windowStart + model.clear.startIndex * MIN, displayTimezone)}`
                  : 'No idle window in range'
              }
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>{horizonHours === 24 ? 'Next 24 hours' : 'Next 7 days'}</CardTitle>
              <CardDescription>
                {formatInstant(viewStart, displayTimezone)} →{' '}
                {formatInstant(viewEnd, displayTimezone)}
                {zoom ? ' (zoomed)' : ''}. Shown in {displayTimezone}; schedules are evaluated in{' '}
                {data.schedule_timezone}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-[236px_minmax(0,1fr)]">
                <div className="pr-4">
                  <div className="font-medium">Concurrent runs</div>
                  <div className="text-xs text-muted-foreground">
                    Whole horizon. Drag to zoom the lanes below.
                  </div>
                </div>
                <ConcurrencyRibbon
                  eventDepth={model.eventDepth}
                  baselineDepth={model.baselineDepth}
                  windowStart={model.windowStart}
                  windowEnd={model.windowEnd}
                  viewStart={viewStart}
                  viewEnd={viewEnd}
                  onZoom={handleZoom}
                />
              </div>

              <div className="grid grid-cols-[236px_minmax(0,1fr)]">
                <div />
                <ContentionChips
                  ranges={model.ranges}
                  windowStart={model.windowStart}
                  displayTimezone={displayTimezone}
                  multiDay={horizonHours > 24}
                  zoomed={zoom !== null}
                  onZoom={(start, end) => {
                    const pad = Math.max((end - start) * 0.6, 20 * MIN);
                    setZoom({ start: start - pad, end: end + pad });
                  }}
                  onReset={() => setZoom(null)}
                />
              </div>

              <ScheduleTimeline
                lanes={model.events}
                viewStart={viewStart}
                viewEnd={viewEnd}
                windowStart={model.windowStart}
                contention={model.ranges.map((r) => [r.from, r.to] as [number, number])}
                displayTimezone={displayTimezone}
                now={model.windowStart}
                describeSchedule={(lane) => describeCron(lane.cron, lane.timezone)}
              />

              {/* Connectors that start more often than hourly are background hum,
                  not events to plan around: they form the ribbon's grey baseline
                  rather than lanes. No connector schedules sub-hourly today, so
                  this renders nothing — but it must never drop them silently. */}
              {model.continuous.length > 0 && (
                <div className="grid grid-cols-[236px_minmax(0,1fr)] border-b bg-muted/45">
                  <div className="py-2.5 pr-4">
                    <div className="font-medium">Continuous</div>
                    <div className="text-xs text-muted-foreground">
                      {model.continuous.length} connectors ·{' '}
                      {model.continuous.reduce((n, l) => n + l.runs.length, 0)} runs
                    </div>
                  </div>
                  <div className="flex items-center py-2.5 text-xs text-muted-foreground">
                    {model.continuous
                      .map(
                        (l) =>
                          `${l.name} (${describeCron(l.cron, l.timezone).toLowerCase()}, ${formatDuration(l.medianSeconds)})`,
                      )
                      .join(' · ')}
                  </div>
                </div>
              )}

              <p className="pt-4 text-xs text-muted-foreground">
                Bar length is the median duration of completed scheduled runs over the last{' '}
                {data.lookback_days} days. A connector with no completed runs in that window has a
                bar that fades out — the start is known but the duration is not — and is left out of
                the concurrency count.
              </p>
            </CardContent>
          </Card>

          {data.excluded.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Not Projected</CardTitle>
                <CardDescription>
                  Connectors left out of the timeline above, and why.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <NotProjectedTable excluded={data.excluded} />
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default ScheduleTimelinePage;
